import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { formatNumber } from '../helpers/ChartHelper';
import moment from 'moment';
import { SkeletonLoader } from './loading/SkeletonLoader';
// import { formatNumber } from '../helpers/ChartHelper';
// import moment from 'moment';
// import { UserFeatureEnum, UserFeature } from '../types/UserFeature';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Explicit request to modernise this - same rounded-card treatment as
        // EnquiryFormDrawer.tsx's own infoPanel (8px radius, soft shadow, 1px border)
        // instead of the old borderless, cramped-padding table.
        root: {
            display: 'flex',
            flex: 1,
            flexFlow: 'column',
            borderRadius: 8,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            backgroundColor: '#fff',
        },
        headerWrapper: {
            display: 'flex',
            flex: 1,
            backgroundColor: 'rgb(247, 248, 250)',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            '& > div': {
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '11px',
                fontWeight: 700,
                opacity: 0.6,
                color: 'rgb(35, 37, 59)'
            },
            '& > div:nth-child(1)': {
                opacity: 1,
                color: 'inherit',
            },
        },
        colHeader: {
            flex: 1,
            padding: '12px 16px',
        },
        bodyWrapper: {
            display: 'flex',
            flex: 1,
            flexFlow: 'column',
            '& > div:nth-child(odd)': {
                backgroundColor: 'rgb(247, 248, 250)'
            }
        },
        rowBody: {
            flex: 1,
            display: 'flex',
            '& > div': {
                textTransform: 'capitalize',
                opacity: 0.52,
                color: 'rgb(35, 37, 59)'
            },
            '& > div:nth-child(1)': {
                opacity: 1,
                color: 'inherit',
                fontWeight: 600,
            },
        },
        colBody: {
            flex: 1,
            padding: '12px 16px',
            fontSize: '13px',
        },
        imagesWrapper: {
            display: 'flex',
            justifyContent: 'flex-start',
            flexFlow: 'row wrap',
            margin: '0 -10px',
            '& img': {
                width: '100%',
                height: 'auto',
                maxWidth: '35px',
                padding: '0 10px',
            }
        }
    })
);

export enum ValueTypeEnum {
    STRING = 0,
    DECIMAL,
    NUMBER,
    DATE,
    ARRAY,
    ARRAY_OF_OBJECT,
    OBJECT,
    IMAGE,
    ARRAY_OF_IMAGE
}   

export interface CustomTableValueProps {
    // tslint:disable-next-line: no-any
    value: any;
    type: ValueTypeEnum;
}

export interface CustomTableContentProps {
    // tslint:disable-next-line: no-any
    [key: string]: CustomTableValueProps | any;
}

interface CustomTableProps {
    header?: string[];
    content?: CustomTableContentProps[];
    options?: string[];
}

// tslint:disable-next-line: no-any
export const CustomTable: React.FC<CustomTableProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line: no-any
    const formatValue = (item: CustomTableValueProps) => {
        if (item.type === ValueTypeEnum.NUMBER) {
            return formatNumber(item.value);
        }
        if (item.type === ValueTypeEnum.DECIMAL) {
            return formatNumber(item.value, 2);
        }
        if (item.type === ValueTypeEnum.DATE) {
            return moment(item.value).format('DD-MMM-YYYY');
        }
        if (item.type === ValueTypeEnum.ARRAY) {
            return item.value.join(',');
        }
        if (item.type === ValueTypeEnum.IMAGE) {
            return (
                <img src={item.value} alt={item.value}/>
            );
        }
        if (item.type === ValueTypeEnum.ARRAY_OF_IMAGE) {
            return (
                <div className={classes.imagesWrapper}>
                    {item.value.map((val: string, index: number) => (
                        <img src={val} alt={index.toString()} />
                    ))}
                </div>
            );
        }
        return item.value;
    };

    const getContent = () => {
        if (props.content) {
            return (
                <>
                    {props.content && props.content
                    .sort((a, b) => a.order < b.order ? -1 : a.order > b.order ? 1 : 0)
                    .map((item: CustomTableContentProps, index: number) => !item.hide && (
                        <div className={classes.rowBody} key={index}>
                            {props.options && props.options.map((option: string, i: number) => (
                                <div className={`${classes.colBody} label-${option}`} key={i}>
                                    {formatValue(item[option])}
                                </div>
                            ))}
                        </div>
                    ))}
                </>
            );
        }
        // Explicit bug report: this showed a generic spinner while content hadn't loaded yet -
        // project convention is a SkeletonLoader everywhere. Matches the real rowBody/colBody
        // structure (one cell per option) so the loading state lines up with what replaces it.
        const skeletonColumns = props.options && props.options.length > 0 ? props.options : [''];
        return (
            <>
                {Array.from({ length: 2 }).map((_, rowIndex) => (
                    <div className={classes.rowBody} key={rowIndex}>
                        {skeletonColumns.map((_option, colIndex) => (
                            <div className={`${classes.colBody}`} key={colIndex}>
                                <SkeletonLoader variant="shape" shape="text" />
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.headerWrapper}>
                {props.header && props.header.map((head, index) => (
                    <div className={classes.colHeader} key={index}>
                        {head}
                    </div>
                ))}
            </div>
            <div className={classes.bodyWrapper}>
                {getContent()}
            </div>
        </div>
    );
};
